import { useEffect, useState } from "react";
import { api, euro } from "../lib/api";
import { toast } from "sonner";
import { X, Phone, Mail, MessageCircle, Loader2 } from "lucide-react";

export const DEAL_STATUSES = ["Nuovo", "Da richiamare", "Contattato", "Appuntamento fissato", "In trattativa", "Venduto", "Perso"];

export const dealStatusCls = (s) => ({
  "Nuovo": "bg-secondary text-ink",
  "Da richiamare": "bg-sand/20 text-ink",
  "Contattato": "bg-sky-100 text-sky-800",
  "Appuntamento fissato": "bg-indigo-100 text-indigo-800",
  "In trattativa": "bg-amber-100 text-amber-800",
  "Venduto": "bg-forest/10 text-forest",
  "Perso": "bg-clay/10 text-clay",
}[s] || "bg-secondary text-ink");

export const payCls = (s) => ({
  paid: "bg-forest/10 text-forest",
  initiated: "bg-amber-100 text-amber-800",
  failed: "bg-clay/10 text-clay",
  expired: "bg-clay/10 text-clay",
  refunded: "bg-sky-100 text-sky-800",
}[s] || "bg-secondary text-ink-soft");

const fmt = (d) => (d ? new Date(d).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");
const val = (v) => (v === null || v === undefined || v === "" ? "—" : typeof v === "boolean" ? (v ? "Sì" : "No") : String(v));
const waLink = (phone) => { const n = String(phone || "").replace(/\D/g, ""); return `https://wa.me/${n.startsWith("39") && n.length > 10 ? n : "39" + n.replace(/^0+/, "")}`; };

function Section({ title, children, testid }) {
  return (
    <section data-testid={testid} className="bg-white border border-border rounded-2xl p-4 md:p-5">
      <h3 className="text-xs uppercase tracking-wider text-ink-soft mb-3">{title}</h3>
      {children}
    </section>
  );
}

function Rows({ rows }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-3 border-b border-border/60 py-1.5">
          <dt className="text-ink-soft">{k}</dt><dd className="font-medium text-right break-words">{val(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

export function LeadDetail({ leadId, onClose, onChanged }) {
  const [l, setL] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/leads/${leadId}`).then((r) => { setL(r.data); setNotes(r.data.internal_notes || ""); })
      .catch(() => { toast.error("Cliente non trovato"); onClose(); });
  }, [leadId]);

  const patch = async (body, msg) => {
    setSaving(true);
    try { const r = await api.patch(`/admin/leads/${leadId}`, body); setL(r.data); toast.success(msg); onChanged?.(); }
    catch { toast.error("Errore nel salvataggio"); }
    setSaving(false);
  };

  const c = l?.customer || {};
  const p = l?.permuta;
  const f = l?.financing;
  const v = l?.vehicle;
  const ad = l?.action_details || {};
  const known = new Set(["has_permuta", "make", "model", "version", "plate", "year", "km", "residual", "condition", "photos"]);
  const extraPermuta = p ? Object.entries(p).filter(([k]) => !known.has(k)) : [];

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-end md:items-center justify-center p-0 md:p-6" onClick={onClose}>
      <div data-testid="lead-detail" className="bg-background w-full md:max-w-3xl rounded-t-3xl md:rounded-3xl max-h-[94vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 glass border-b border-border px-5 md:px-6 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 data-testid="lead-detail-name" className="font-serif text-2xl truncate">{c.name || "Cliente senza nome"}</h2>
            <div className="text-xs text-ink-soft">{l ? `${l.vehicle_label} · richiesta del ${fmt(l.created_at)}` : ""}</div>
          </div>
          <button data-testid="lead-detail-close" onClick={onClose} className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center hover:bg-secondary"><X size={18} /></button>
        </div>

        {!l ? <div className="p-10 flex justify-center"><Loader2 className="animate-spin" /></div> : (
          <div className="p-4 md:p-6 space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <a data-testid="lead-call-btn" href={c.phone ? `tel:${c.phone}` : undefined} aria-disabled={!c.phone}
                className={`h-12 rounded-xl flex items-center justify-center gap-2 text-sm border border-border bg-white hover:bg-secondary ${!c.phone && "opacity-40 pointer-events-none"}`}><Phone size={16} /> Chiama</a>
              <a data-testid="lead-whatsapp-btn" href={c.phone ? waLink(c.phone) : undefined} target="_blank" rel="noreferrer"
                className={`h-12 rounded-xl flex items-center justify-center gap-2 text-sm bg-[#25D366] text-white hover:opacity-90 ${!c.phone && "opacity-40 pointer-events-none"}`}><MessageCircle size={16} /> WhatsApp</a>
              <a data-testid="lead-email-btn" href={c.email ? `mailto:${c.email}` : undefined}
                className={`h-12 rounded-xl flex items-center justify-center gap-2 text-sm bg-ink text-white hover:opacity-90 ${!c.email && "opacity-40 pointer-events-none"}`}><Mail size={16} /> Email</a>
            </div>

            <Section title="Stato trattativa" testid="lead-deal-section">
              <div className="flex flex-wrap gap-2">
                {DEAL_STATUSES.map((s) => (
                  <button key={s} data-testid={`deal-status-${s.replace(/\s+/g, "-").toLowerCase()}`} disabled={saving}
                    onClick={() => s !== l.deal_status && patch({ deal_status: s }, `Stato: ${s}`)}
                    className={`px-3 py-2 rounded-full text-xs border transition-colors ${l.deal_status === s ? "bg-ink text-white border-ink" : "bg-white border-border hover:bg-secondary"}`}>{s}</button>
                ))}
              </div>
            </Section>

            <Section title="Cliente" testid="lead-customer-section">
              <Rows rows={[["Nome e cognome", c.name], ["Telefono", c.phone], ["Email", c.email],
                ["Data e ora richiesta", fmt(l.created_at)], ["Ultimo aggiornamento", fmt(l.updated_at)],
                ["Fase raggiunta", l.phase_label], ["Codice ripresa", l.resume_token]]} />
            </Section>

            <Section title="Vettura interessata" testid="lead-vehicle-section">
              {v ? (
                <div className="flex gap-4 items-center">
                  {v.images?.[0] && <img src={v.images[0]} alt="" className="w-24 h-18 object-cover rounded-xl" />}
                  <div className="text-sm">
                    <div className="font-medium">{v.make} {v.model} {v.version}</div>
                    <div className="text-ink-soft">{v.year} · {v.km?.toLocaleString("it-IT")} km · {v.fuel}</div>
                    <div className="mt-1">{euro(v.price)}{v.monthly_rate ? ` · da ${euro(v.monthly_rate)}/mese` : ""}</div>
                  </div>
                </div>
              ) : <div className="text-sm text-ink-soft">Vettura non più presente</div>}
            </Section>

            <Section title="Permuta" testid="lead-permuta-section">
              {!p ? <div className="text-sm text-ink-soft">Non ancora risposto</div>
                : !p.has_permuta ? <div className="text-sm">Nessuna permuta</div>
                : <Rows rows={[["Marca", p.make], ["Modello", p.model], ["Versione", p.version], ["Targa", p.plate], ["Anno", p.year],
                  ["Km", p.km], ["Finanziamento residuo", p.residual ? euro(+p.residual) : "—"], ["Condizioni", p.condition],
                  ["Fotografie", Array.isArray(p.photos) ? `${p.photos.length} file` : p.photos ? "Sì" : "—"], ...extraPermuta]} />}
            </Section>

            <Section title="Modalità di acquisto" testid="lead-purchase-section">
              <Rows rows={[["Modalità scelta", l.purchase_mode],
                ...(f ? [["Anticipo", f.down_payment !== "" && f.down_payment != null ? euro(+f.down_payment) : "—"], ["Durata", f.duration ? `${f.duration} mesi` : "—"],
                  ["Rata indicativa", f.monthly ? `${euro(f.monthly)}/mese` : "—"], ["Assicurazione inclusa", f.insurance]] : []),
                ...(l.purchase_mode === "Leasing" ? [["Leasing richiesto", true]] : [])]} />
            </Section>

            <Section title="Deposito e pagamenti" testid="lead-payments-section">
              {(() => {
                const paid = l.payments?.find((t) => t.payment_status === "paid");
                const confirmed = l.blocks?.some((b) => b.confirmed && b.status === "active");
                const overall = paid ? "Pagato" : l.payments?.some((t) => t.payment_status === "initiated") ? "Avviato (non completato)"
                  : l.payments?.some((t) => ["failed", "expired"].includes(t.payment_status)) ? "Fallito"
                  : l.payments?.some((t) => t.payment_status === "refunded") ? "Rimborsato" : "Non avviato";
                return (
                  <div className="flex flex-wrap items-center gap-2 mb-3 text-sm">
                    <span className="text-ink-soft">Stato deposito:</span>
                    <span data-testid="lead-payment-overall" className={`px-2 py-0.5 rounded-full text-xs ${payCls(paid ? "paid" : overall === "Avviato (non completato)" ? "initiated" : overall === "Fallito" ? "failed" : overall === "Rimborsato" ? "refunded" : "")}`}>{overall}</span>
                    <span data-testid="lead-vehicle-blocked" className={`px-2 py-0.5 rounded-full text-xs ${confirmed ? "bg-forest/10 text-forest" : "bg-secondary text-ink-soft"}`}>{confirmed ? "Vettura bloccata" : "Vettura non bloccata"}</span>
                  </div>
                );
              })()}
              {!l.payments?.length && !l.blocks?.length && <div className="text-sm text-ink-soft">Nessun pagamento avviato</div>}
              {l.payments?.length > 0 && (
                <div className="space-y-1.5 text-sm">
                  {l.payments.map((t) => (
                    <div key={t.id} data-testid={`lead-payment-${t.id}`} className="flex flex-wrap justify-between gap-2 border-t border-border/60 pt-1.5">
                      <span className="text-ink-soft">{fmt(t.paid_at || t.failed_at || t.created_at)} · {euro(t.amount)}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${payCls(t.payment_status)}`}>{t.payment_label}</span>
                    </div>
                  ))}
                </div>
              )}
              {l.blocks?.length > 0 && (
                <div className="mt-3 space-y-1 text-sm">
                  {l.blocks.map((b) => <div key={b.id} className="flex flex-wrap justify-between gap-2 border-t border-border/60 pt-1.5"><span className="text-ink-soft">Blocco via {b.method === "card" ? "carta" : "bonifico"} · {b.status === "active" ? "attivo" : "scaduto"} · {b.confirmed ? "confermato" : "in attesa di pagamento"}</span><span className="font-medium">{euro(b.amount)} · {fmt(b.created_at)}</span></div>)}
                </div>
              )}
            </Section>

            <Section title="Richieste e azioni" testid="lead-actions-section">
              <Rows rows={[["Preventivo", l.quote_at ? fmt(l.quote_at) : "Non richiesto"], ["Videochiamata", l.videocall_at ? fmt(l.videocall_at) : "Non richiesta"],
                ["Appuntamento", l.appointment_at ? fmt(l.appointment_at) : "Non richiesto"], ["Blocco vettura", l.block_at ? fmt(l.block_at) : "Non richiesto"],
                ...Object.entries(ad).map(([k, vv]) => [k, typeof vv === "object" ? JSON.stringify(vv) : vv])]} />
            </Section>

            <Section title="Note interne" testid="lead-notes-section">
              <textarea data-testid="lead-notes-input" value={notes} rows={5} maxLength={4000} onChange={(e) => setNotes(e.target.value)}
                placeholder="Appunti visibili solo al concessionario…"
                className="w-full p-3 rounded-xl border border-border outline-none focus:border-ink text-base leading-relaxed resize-y" />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-ink-soft tabular-nums">{notes.length} / 4.000</span>
                <button data-testid="lead-notes-save" disabled={saving || notes === (l.internal_notes || "")} onClick={() => patch({ internal_notes: notes }, "Note salvate")}
                  className="px-5 py-2.5 rounded-full bg-ink text-white text-sm hover:opacity-90 disabled:opacity-40">{saving ? <Loader2 size={14} className="animate-spin" /> : "Salva note"}</button>
              </div>
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}
